import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, message, Input, Modal, Alert, Row, Col, Statistic, Layout, Menu, Radio } from 'antd';
import { SearchOutlined, ReloadOutlined, SyncOutlined, CloseOutlined, TeamOutlined, AppstoreOutlined } from '@ant-design/icons';
import { clientApi, tradingApi, groupApi } from '../services/api';
import { useAuth } from '../utils/auth';

const { Sider, Content } = Layout;

// 今日委托：展示今日委托，支持分组筛选、统计、搜索、撤单、同步
export default function TodayOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all'); // 'all' = 全部分组
  const [statusFilter, setStatusFilter] = useState('all'); // all | filled(已成交) | unfilled(未成交)
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isSuper = user?.is_super_admin === 1 || user?.role === 'admin';

  const loadClients = async () => {
    try {
      const res = await clientApi.getOnline();
      if (res.success) setClients(res.data || []);
    } catch { /* ignore */ }
  };

  const loadGroups = async () => {
    try {
      const res = await groupApi.list();
      if (res.success) {
        let gs = res.data || [];
        // 普通用户只展示自己的分组
        if (!isSuper && user?.group_id) {
          gs = gs.filter(g => g.id === user.group_id);
        }
        setGroups(gs);
      }
    } catch { /* ignore */ }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await clientApi.getTodayOrders('');
      if (res.success) setOrders(res.data?.today_orders || []);
    } catch (e) { console.error('[TodayOrdersPage] loadOrders error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadClients();
    loadGroups();
    loadOrders();
  }, []);

  const getClientName = (id) => {
    const c = clients.find(x => x.id === id);
    return c ? c.name || id : id;
  };

  // 构建分组树：顶层分组（parent_id 为空）为一级，子分组作为 children 缩进显示
  // 每个节点的 client_id_list 累加自身 + 所有子孙（去重），使"选主分组 = 选其下所有子分组"
  const treeGroups = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined, selfClientIds: g.client_id_list || [] }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else {
        roots.push(map[g.id]);
      }
    });
    const accumulate = (node) => {
      let all = [...(node.selfClientIds || [])];
      (node.children || []).forEach(ch => { all = all.concat(accumulate(ch)); });
      const uniq = [];
      const seen = new Set();
      all.forEach(id => { if (!seen.has(id)) { seen.add(id); uniq.push(id); } });
      node._clientIds = new Set(uniq);
      return uniq;
    };
    roots.forEach(accumulate);
    return roots;
  }, [groups]);

  // groupId -> 该分组及其所有子孙的 client_id 集合（用于筛选/计数）
  const groupClientIdSet = useMemo(() => {
    const m = {};
    const walk = (node) => {
      m[node.id] = node._clientIds;
      (node.children || []).forEach(walk);
    };
    treeGroups.forEach(walk);
    return m;
  }, [treeGroups]);

  // 按分组过滤后的委托（选中主分组时包含其所有子孙分组委托）
  const groupFilteredOrders = useMemo(() => {
    if (selectedGroup === 'all') return orders;
    const ids = groupClientIdSet[selectedGroup];
    if (!ids) return orders;
    return orders.filter(o => ids.has(o.client_id));
  }, [orders, selectedGroup, groupClientIdSet]);

  const isCancellable = (o) => {
    const st = o.status || '';
    if (['已成', '全部成交', '已撤', '全部撤单', '废单', '已删除'].includes(st)) return false;
    if (st.includes('已成') || st.includes('已撤') || st.includes('废单')) return false;
    return true;
  };

  // 是否已成交（全部成交）
  const isFilledOrder = (o) => {
    const st = o.status || '';
    const qty = o.quantity || 0;
    const filledQty = o.filled_qty || 0;
    return st.includes('已成') || st.includes('全部成交') || (qty > 0 && filledQty > 0 && filledQty >= qty);
  };

  const cancellableOrders = useMemo(() => groupFilteredOrders.filter(isCancellable), [groupFilteredOrders]);

  // 按成交状态筛选
  const statusFilteredOrders = useMemo(() => {
    if (statusFilter === 'filled') return groupFilteredOrders.filter(isFilledOrder);
    if (statusFilter === 'unfilled') return groupFilteredOrders.filter(o => !isFilledOrder(o));
    return groupFilteredOrders;
  }, [groupFilteredOrders, statusFilter]);

  // 可撤单委托排前面（其余按时间倒序）
  const sortedOrders = useMemo(() => {
    return [...statusFilteredOrders].sort((a, b) => {
      const aCan = isCancellable(a) ? 1 : 0;
      const bCan = isCancellable(b) ? 1 : 0;
      if (aCan !== bCan) return bCan - aCan;
      return (b.created_at || '') > (a.created_at || '') ? 1 : -1;
    });
  }, [statusFilteredOrders]);

  const filteredOrders = useMemo(() => {
    if (!search) return sortedOrders;
    const s = search.toLowerCase();
    return sortedOrders.filter(o =>
      (o.client_id || '').toLowerCase().includes(s) ||
      (getClientName(o.client_id) || '').toLowerCase().includes(s) ||
      (o.stock_code || '').toLowerCase().includes(s) ||
      (o.stock_name || '').toLowerCase().includes(s) ||
      (o.id || '').toLowerCase().includes(s)
    );
  }, [sortedOrders, search, clients]);

  // 统计（基于当前分组过滤后的委托）
  const stats = useMemo(() => {
    let totalAmount = 0;      // 总操作金额 = Σ(委托价×委托数量)
    let filledAmount = 0;     // 成交金额
    let filledCount = 0;      // 成交单数量
    let unFilledAmount = 0;   // 未成交金额
    let unFilledQty = 0;      // 未成交数量
    groupFilteredOrders.forEach(o => {
      const price = o.price || 0;
      const qty = o.quantity || 0;
      const filledQty = o.filled_qty || 0;
      const status = o.status || '';
      totalAmount += price * qty;
      if (o.filled_amount != null && o.filled_amount > 0) {
        filledAmount += o.filled_amount;
      } else {
        filledAmount += price * filledQty;
      }
      const isFilled = status.includes('已成') || status.includes('全部成交') || (filledQty > 0 && filledQty >= qty);
      if (isFilled) filledCount++;
      unFilledQty += Math.max(qty - filledQty, 0);
      unFilledAmount += price * Math.max(qty - filledQty, 0);
    });
    return { totalAmount, filledAmount, filledCount, unFilledAmount, unFilledQty };
  }, [groupFilteredOrders]);

  const handleSync = async () => {
    setSyncing(true);
    message.loading({ content: '正在同步当日委托...', key: 'syncToday' });
    try {
      const res = await clientApi.syncAllTodayOrders();
      message.destroy('syncToday');
      if (res.success) {
        const { synced = 0, total = 0 } = res.data || {};
        message.success(`同步完成：成功 ${synced}/${total}`);
        setTimeout(loadOrders, 2000);
      } else {
        message.error(res.message || '同步失败');
      }
    } catch {
      message.destroy('syncToday');
      message.error('同步请求失败');
    }
    setSyncing(false);
  };

  const handleCancelOne = async (order) => {
    try {
      const res = await tradingApi.cancelTodayOrder(order.id, order.client_id);
      if (res.success) {
        message.success('撤单成功');
        setTimeout(loadOrders, 1500);
      } else {
        message.error(res.message || '撤单失败');
      }
    } catch {
      message.error('撤单请求失败');
    }
  };

  const handleCancelAll = async () => {
    const ids = cancellableOrders.map(o => o.id);
    if (ids.length === 0) { message.info('没有可撤单的委托'); return; }
    setCancelling(true);
    try {
      const res = await tradingApi.cancelTodayOrders(ids);
      if (res.success) {
        const { success = 0, failed = [], total = 0 } = res.data || {};
        message.success(`撤单完成：成功 ${success}/${total}${failed.length ? `，${failed.length} 笔失败` : ''}`);
        if (failed.length > 0) {
          Modal.warning({ title: '部分撤单失败', content: failed.join('\n'), width: 500 });
        }
        setTimeout(loadOrders, 1500);
      } else {
        message.error(res.message || '撤单失败');
      }
    } catch {
      message.error('撤单请求失败');
    }
    setCancelling(false);
    setCancelModal(false);
  };

  const columns = [
    { title: '客户端', dataIndex: 'client_id', width: 130, render: (v) => {
      const name = getClientName(v);
      return <Tag>{name === v ? v : `${name}(${v})`}</Tag>;
    }},
    { title: '委托号', dataIndex: 'id', width: 150, ellipsis: true, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: '日期', dataIndex: 'order_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 120, render: (_, r) => <span><b>{r.stock_name}</b><br /><small>{r.stock_code}</small></span> },
    { title: '方向', dataIndex: 'direction', width: 55, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '类型', dataIndex: 'order_type', width: 70, render: v => v || '-' },
    { title: '价格', dataIndex: 'price', width: 70, render: v => (v ? v.toFixed(2) : '市价') },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '成交', dataIndex: 'filled_qty', width: 65, render: v => v?.toLocaleString() || 0 },
    { title: '状态', dataIndex: 'status', width: 75, render: v => {
      const colorMap = { '已成': 'success', '已撤': 'default', '已报': 'processing', '部成': 'warning', '废单': 'error', '全部成交': 'success', '全部撤单': 'default' };
      let color = 'default';
      if (v) {
        for (const [k, cc] of Object.entries(colorMap)) {
          if (v.includes(k)) { color = cc; break; }
        }
      }
      return <Tag color={color}>{v || '-'}</Tag>;
    }},
    { title: '操作', key: 'action', width: 80, fixed: 'right', render: (_, r) => {
      const cancellable = isCancellable(r);
      return (
        <Button size="small" danger disabled={!cancellable} icon={<CloseOutlined />}
          onClick={() => {
            Modal.confirm({
              title: `确认撤单 ${r.stock_name}(${r.stock_code}) 委托？`,
              content: `${getClientName(r.client_id)} · ${r.direction} · 数量 ${r.quantity}`,
              okText: '撤单', cancelText: '取消',
              onOk: () => handleCancelOne(r),
            });
          }}
        >撤单</Button>
      );
    }},
  ];

  // 构建分组树：顶层分组（parent_id 为空）为一级，子分组作为 children 缩进显示
  // 每个节点的 client_id_list 累加自身 + 所有子孙（去重），使"选主分组 = 选其下所有子分组"
  // 分组菜单项（树形）
  const menuItems = useMemo(() => {
    const buildItems = (nodes) => nodes.map(g => {
      const ids = groupClientIdSet[g.id] || new Set();
      const count = (orders || []).filter(o => ids.has(o.client_id)).length;
      const item = {
        key: g.id,
        icon: <TeamOutlined />,
        label: <span>{g.name} <Tag style={{ marginLeft: 4 }}>{count}</Tag></span>,
      };
      if (g.children && g.children.length) item.children = buildItems(g.children);
      return item;
    });
    return [
      { key: 'all', icon: <AppstoreOutlined />, label: <span>全部委托 <Tag style={{ marginLeft: 4 }}>{orders.length}</Tag></span> },
      ...buildItems(treeGroups),
    ];
  }, [treeGroups, groupClientIdSet, orders]);

  const currentGroupName = selectedGroup === 'all' ? '全部委托' : (groups.find(g => g.id === selectedGroup)?.name || '全部委托');

  return (
    <Layout style={{ background: 'transparent' }}>
      <Sider width={220} theme="light" style={{ background: '#fff', marginRight: 12, borderRight: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 0' }}>
        <div style={{ padding: '8px 16px', fontWeight: 600, color: '#666', fontSize: 13 }}>委托分组</div>
        <Menu
          mode="inline"
          selectedKeys={[selectedGroup]}
          defaultOpenKeys={treeGroups.map(g => g.id)}
          items={menuItems}
          onClick={({ key }) => setSelectedGroup(key)}
          style={{ borderInlineEnd: 'none' }}
        />
      </Sider>
      <Content>
        <Card
          title={<Space><span style={{ fontWeight: 600 }}>{currentGroupName}</span><Tag color="blue">{statusFilteredOrders.length} 笔</Tag><Tag color="orange">可撤 {cancellableOrders.length} 笔</Tag></Space>}
          size="small"
          loading={loading}
          extra={
            <Space wrap>
              <Radio.Group size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '已成交', value: 'filled' },
                  { label: '未成交', value: 'unfilled' },
                ]}
                optionType="button" buttonStyle="solid"
              />
              <Input.Search placeholder="搜索客户端ID/名称/股票代码/委托号" value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: 280 }} allowClear prefix={<SearchOutlined />} />
              <Button size="small" icon={<ReloadOutlined />} onClick={() => { loadOrders(); loadClients(); loadGroups(); }}>刷新</Button>
              <Button size="small" icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>同步当日委托</Button>
              <Button size="small" danger icon={<CloseOutlined />} disabled={cancellableOrders.length === 0}
                onClick={() => setCancelModal(true)}>全部撤单</Button>
            </Space>
          }
        >
          {/* 统计卡片 */}
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col span={5}><Card size="small"><Statistic title="总操作金额" value={stats.totalAmount} precision={2} prefix="¥" /></Card></Col>
            <Col span={5}><Card size="small"><Statistic title="成交金额" value={stats.filledAmount} precision={2} prefix="¥" valueStyle={{ color: '#2980b9' }} /></Card></Col>
            <Col span={5}><Card size="small"><Statistic title="成交单数量" value={stats.filledCount} suffix="单" valueStyle={{ color: '#27ae60' }} /></Card></Col>
            <Col span={5}><Card size="small"><Statistic title="未成交金额" value={stats.unFilledAmount} precision={2} prefix="¥" valueStyle={{ color: '#e67e22' }} /></Card></Col>
            <Col span={4}><Card size="small"><Statistic title="未成交数量" value={stats.unFilledQty} suffix="股" valueStyle={{ color: '#e74c3c' }} /></Card></Col>
          </Row>

          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`共有 ${cancellableOrders.length} 笔委托可撤单`} />
          <Table dataSource={filteredOrders} columns={columns} rowKey="id" size="small"
            pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: t => `共 ${t} 笔` }}
            scroll={{ y: 460 }} locale={{ emptyText: '暂无委托数据' }} />
        </Card>
      </Content>

      {/* 全部撤单确认弹窗：列出可撤单委托 */}
      <Modal
        title={`可撤单委托列表 (${cancellableOrders.length})`}
        open={cancelModal}
        onCancel={() => setCancelModal(false)}
        onOk={handleCancelAll}
        okText="确认全部撤单"
        cancelText="取消"
        confirmLoading={cancelling}
        width={720}
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`确认对这 ${cancellableOrders.length} 笔可撤单委托全部执行撤单？`} />
        <Table
          dataSource={cancellableOrders}
          columns={[
            { title: '客户端', dataIndex: 'client_id', width: 120, render: v => getClientName(v) },
            { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
            { title: '方向', dataIndex: 'direction', width: 55, render: v => {
              const isBuy = v && (v.includes('买入') || v === 'buy');
              return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
            }},
            { title: '价格', dataIndex: 'price', width: 70, render: v => (v ? v.toFixed(2) : '市价') },
            { title: '数量', dataIndex: 'quantity', width: 60, render: v => v?.toLocaleString() },
            { title: '状态', dataIndex: 'status', width: 70, render: v => v || '-' },
          ]}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          scroll={{ y: 320 }}
          locale={{ emptyText: '暂无可撤单委托' }}
        />
      </Modal>
    </Layout>
  );
}
